import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiFacebook, 
  FiTwitter, 
  FiLinkedin, 
  FiInstagram, 
  FiMail, 
  FiPhone, 
  FiMapPin,
  FiYoutube
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useSettings } from '../contexts/SettingsContext';

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.dark};
  color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['4xl']} 0 ${props => props.theme.spacing.xl} 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing['2xl']} 0 ${props => props.theme.spacing.lg} 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing.md} 0;
  }
`;

const FooterContent = styled.div`
  /* Align with header nav width and padding */
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: ${props => props.theme.spacing['2xl']};
  margin-bottom: ${props => props.theme.spacing['2xl']};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    /* Mobile layout: Company spans, Quick & Courses side-by-side, Contact centered full width */
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'company company'
      'quick courses'
      'contact contact';
    gap: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.xl};

    > div:nth-child(1) { grid-area: company; }
    > div:nth-child(2) { grid-area: quick; }
    > div:nth-child(3) { grid-area: courses; }
    > div:nth-child(4) { 
      grid-area: contact; 
      text-align: center;
    }

    /* Center the decorative underline under 'Contact Info' when centered */
    > div:nth-child(4) h3::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${props => props.theme.colors.light};
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: ${props => props.theme.fontWeights.semibold};
    margin-bottom: ${props => props.theme.spacing.lg};
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -${props => props.theme.spacing.sm};
      left: 0;
      width: 40px;
      height: 2px;
      background: ${props => props.theme.colors.primary};
    }
  }

  p {
    color: ${props => props.theme.colors.gray[300]};
    line-height: 1.6;
    margin-bottom: ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    h3 {
      font-size: ${props => props.theme.fontSizes.md};
      margin-bottom: ${props => props.theme.spacing.md};

      &::after {
        width: 32px;
      }
    }

    p {
      font-size: ${props => props.theme.fontSizes.sm};
    }
  }
`;

const CompanyInfo = styled.div`
  .logo {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.lg};
    /* Nudge logo left like header logo to align visually */
    margin-left: calc(-1 * ${props => props.theme.spacing.lg});

    .logo-image {
      width: 56px;
      height: 56px;
      object-fit: contain;
      border-radius: ${props => props.theme.borderRadius.md};
      background: transparent;
    }

    .logo-text {
      display: flex;
      flex-direction: column;
      font-family: ${props => props.theme.fonts.primary};
      color: ${props => props.theme.colors.light};
      line-height: 1.1;
    }

    .logo-main {
      font-size: ${props => props.theme.fontSizes.xl};
      font-weight: ${props => props.theme.fontWeights.bold};
    }

    .logo-sub {
      font-size: ${props => props.theme.fontSizes.sm};
      color: ${props => props.theme.colors.gray[300]};
      font-weight: ${props => props.theme.fontWeights.medium};
    }

    .highlight {
      color: #ff2600; /* Match 'Hub' brand color */
    }
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    .logo { margin-left: 0; }
    .logo-image { width: 48px; height: 48px; }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    .logo-image { width: 44px; height: 44px; }
    .logo { margin-bottom: ${props => props.theme.spacing.md}; }
    .logo-main { font-size: ${props => props.theme.fontSizes.lg}; }
    .logo-sub { font-size: ${props => props.theme.fontSizes.xs}; }
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: ${props => props.theme.spacing.sm};
  }

  a {
    color: ${props => props.theme.colors.gray[300]};
    text-decoration: none;
    transition: ${props => props.theme.transitions.default};

    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const ContactInfo = styled.div`
  .contact-item {
    display: flex;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.gray[300]};

    svg {
      margin-right: ${props => props.theme.spacing.sm};
      color: ${props => props.theme.colors.primary};
      flex-shrink: 0;
    }

    a {
      color: ${props => props.theme.colors.gray[300]};
      text-decoration: none;
      transition: ${props => props.theme.transitions.default};

      &:hover {
        color: ${props => props.theme.colors.primary};
      }
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    .contact-item { 
      margin-bottom: ${props => props.theme.spacing.sm};
      justify-content: center;
    }
    .contact-item svg { font-size: ${props => props.theme.fontSizes.md}; }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: ${props => props.theme.colors.gray[700]};
    color: ${props => props.theme.colors.light};
    border-radius: ${props => props.theme.borderRadius.full};
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    /* Brand-specific hover colors */
    &.facebook:hover {
      background: #1877F2;
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(24, 119, 242, 0.4);
    }

    &.twitter:hover {
      background: #1DA1F2;
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(29, 161, 242, 0.4);
    }

    &.linkedin:hover {
      background: #0A66C2;
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(10, 102, 194, 0.4);
    }

    &.instagram:hover {
      background: linear-gradient(45deg, #F58529, #DD2A7B, #8134AF, #515BD4);
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(225, 48, 108, 0.4);
    }

    &.youtube:hover {
      background: #FF0000;
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(255, 0, 0, 0.4);
    }

    &.whatsapp:hover {
      background: #25D366;
      transform: translateY(-3px);
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
    }

    svg {
      font-size: ${props => props.theme.fontSizes.lg};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    gap: ${props => props.theme.spacing.sm};

    a {
      width: 36px;
      height: 36px;

      svg { font-size: ${props => props.theme.fontSizes.md}; }
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: ${props => props.theme.spacing.md};

    a {
      width: 32px;
      height: 32px;

      svg { font-size: ${props => props.theme.fontSizes.sm}; }
    }
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${props => props.theme.colors.gray[700]};
  padding-top: ${props => props.theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    text-align: center;
  }

  p {
    color: ${props => props.theme.colors.gray[400]};
    margin: 0;
  }
`;

const LegalLink = styled(Link)`
  color: ${props => props.theme.colors.gray[400]};
  text-decoration: none;
  transition: ${props => props.theme.transitions.default};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

// New: external link style matching LegalLink
const LegalExternalLink = styled.a`
  color: ${props => props.theme.colors.gray[400]};
  text-decoration: none;
  transition: ${props => props.theme.transitions.default};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

// New: group meta links nicely with spacing and responsive wrapping
const FooterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.gray[400]};

  .sep {
    color: ${props => props.theme.colors.gray[700]};
    user-select: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    justify-content: center;
    row-gap: ${props => props.theme.spacing.sm};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.sm};
    font-size: ${props => props.theme.fontSizes.sm};
  }
`;

// New: subtle, compact style for developer credit
const DeveloperCredit = styled(LegalExternalLink)`
  font-size: inherit;
  font-family: inherit;
  opacity: 0.95;
  white-space: nowrap;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSettings();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Courses', path: '/courses' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' }
  ];
  // Use the same courses list as the header dropdown (from settings)
  // to ensure perfect consistency across the site.
  const courses = (settings?.navigation?.courseDropdownItems && Array.isArray(settings.navigation.courseDropdownItems))
    ? settings.navigation.courseDropdownItems
    : [];

  // Social links from settings or defaults
  const formatWhatsAppUrl = (raw) => {
    if (!raw) return '';
    const number = String(raw).replace(/[^\d]/g, '');
    return number ? `https://wa.me/${number}` : '';
  };

  const socialLinks = [
    { 
      icon: FiFacebook, 
      url: settings?.social?.facebook || '#', 
      name: 'Facebook',
      visible: !!settings?.social?.facebook
    },
    { 
      icon: FiTwitter, 
      url: settings?.social?.twitter || '#', 
      name: 'Twitter',
      visible: !!settings?.social?.twitter
    },
    { 
      icon: FiLinkedin, 
      url: settings?.social?.linkedin || '#', 
      name: 'LinkedIn',
      visible: !!settings?.social?.linkedin
    },
    { 
      icon: FiInstagram, 
      url: settings?.social?.instagram || '#', 
      name: 'Instagram',
      visible: !!settings?.social?.instagram
    },
    { 
      icon: FiYoutube, 
      url: settings?.social?.youtube || '#', 
      name: 'YouTube',
      visible: !!settings?.social?.youtube
    },
    {
      icon: FaWhatsapp,
      url: formatWhatsAppUrl(settings?.social?.whatsapp) || '#',
      name: 'WhatsApp',
      visible: !!formatWhatsAppUrl(settings?.social?.whatsapp)
    }
  ].filter(link => link.visible || !settings); // Show all if settings not loaded

  // Contact info from settings or defaults
  const contactInfo = {
    address: settings?.address 
      ? `${settings.address.street || ''}, ${settings.address.city || ''}, ${settings.address.state || ''} ${settings.address.zipCode || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '').trim()
      : '123 Tech Street, IT City, TC 12345',
    phone: settings?.phone || '+1 (234) 567-890',
    phoneSecondary: settings?.phoneSecondary || null,
    email: settings?.email || 'info@servicehubitsolutions.com'
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <CompanyInfo>
              <div className="logo">
                <img src="/logo.png" alt="Service Hub IT Solutions" className="logo-image" />
                <div className="logo-text">
                  <div className="logo-main">Service <span className="highlight">Hub</span></div>
                  <div className="logo-sub">IT Solutions</div>
                </div>
              </div>
              <p>
                {settings?.tagline || 'Empowering minds with cutting-edge technology education. Join thousands of students who have transformed their careers with our comprehensive IT training programs.'}
              </p>
              <SocialLinks>
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      className={social.name.toLowerCase()}
                    >
                      <IconComponent />
                    </a>
                  );
                })}
              </SocialLinks>
            </CompanyInfo>
          </FooterSection>

          <FooterSection>
            <h3>Quick Links</h3>
            <FooterLinks>
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path}>{link.name}</Link>
                </li>
              ))}
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>Our Courses</h3>
            <FooterLinks>
              {courses.map((course) => (
                <li key={course.name}>
                  <Link to={course.path}>{course.name}</Link>
                </li>
              ))}
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <h3>Contact Info</h3>
            <ContactInfo>
              <div className="contact-item">
                <FiMapPin />
                <span>{contactInfo.address}</span>
              </div>
              <div className="contact-item">
                <FiPhone />
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}>{contactInfo.phone}</a>
              </div>
              {contactInfo.phoneSecondary && (
                <div className="contact-item">
                  <FiPhone />
                  <a href={`tel:${contactInfo.phoneSecondary.replace(/\s/g, '')}`}>{contactInfo.phoneSecondary}</a>
                </div>
              )}
              <div className="contact-item">
                <FiMail />
                <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
              </div>
            </ContactInfo>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <p>&copy; {currentYear} Service Hub IT Solutions. All rights reserved.</p>
          <FooterMeta>
            <LegalLink to="/privacy-policy">Privacy Policy</LegalLink>
            <span className="sep">•</span>
            <LegalLink to="/terms-of-service">Terms of Service</LegalLink>
            <span className="sep">•</span>
            <DeveloperCredit href="https://www.cyrtechnologies.com" target="_blank" rel="noopener noreferrer">
              Developed by CYR Technologies
            </DeveloperCredit>
          </FooterMeta>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;